/**
 * AsSpotlight Component
 *
 * Dims (and optionally blurs) the whole app except one element and its
 * children - the highlight step of a walkthrough, or a "look here" hint.
 *
 * How it works, and why it works that way: `filter` and `opacity` apply to a
 * whole subtree, so blurring a shared ancestor would blur the target too, and
 * nothing inside can opt out of it. Instead this paints one full-screen layer
 * over everything and cuts a hole in it with `clip-path`. The app is never
 * restyled, the target stays sharp because nothing is drawn over it, and it
 * stays clickable because a clipped-away area takes no pointer events. Clicks
 * anywhere else land on the layer, which is usually what a walkthrough wants.
 * Since the layer only paints on top, a target inside shadow DOM needs no
 * special handling.
 *
 * It takes no content of its own. A caption, a card or a Next button belongs
 * to the app: put it outside <as-app> and give it a z-index above
 * --spotlight-z-index, and it sits over the dimmed layer, undimmed and
 * clickable. Everything inside <as-app> goes under, whatever its z-index, as
 * as-app is a single stacking context at z-index 1.
 *
 * Attributes:
 *   target        - CSS selector for the element(s) to reveal. Every match
 *                   gets a hole, so `#a, #b` works. Use `>>>` to step into an
 *                   open shadow root: `as-header >>> #undo-button`.
 *   region        - "left,top,width,height" in viewport pixels, to light a
 *                   rectangle instead of (or as well as) an element.
 *   open          - present while the spotlight is showing.
 *   padding       - px of breathing room around the target (default 8).
 *   radius        - px corner radius of the hole (default 8).
 *   blur          - px of backdrop blur (default 3). 0 for a plain dim.
 *   dim           - 0..1 darkness of the dimmed area (default 0.45).
 *   dismissible   - clicking the dimmed area or pressing Escape closes it.
 *   pass-through  - visual only: clicks go through the dimmed area as well.
 *   scroll-into-view - scroll the target into view when shown.
 *
 * CSS variables: --spotlight-dim, --spotlight-blur, --spotlight-z-index,
 *   --spotlight-fade-time, --spotlight-move-time.
 *
 * Events (all bubble and cross shadow boundaries):
 *   as-spotlight-shown         detail: { targets: Element[] }
 *   as-spotlight-hidden
 *   as-spotlight-outside-click
 *   as-spotlight-target-lost   the target left the page; the spotlight closes
 *                              itself rather than trapping the user behind a
 *                              layer with no hole in it.
 *
 * Usage:
 * ```html
 * <as-spotlight target="#importGroup" dismissible open></as-spotlight>
 * ```
 * ```ts
 * import { spotlight, clearSpotlight } from 'as-components';
 * spotlight('#importGroup');
 * spotlight({ left: 200, top: 120, width: 320, height: 240 });  // a region
 * clearSpotlight();
 * ```
 */
import { devlog } from '../../util/utils.ts';
import { AS_SPOTLIGHT_EVENTS, sendEv } from '../../data/events.ts';
import styleText from './as-spotlight.css?raw';

/**
 * A rectangle to light, in viewport coordinates and CSS pixels - the frame
 * getBoundingClientRect reports in. For pointing at part of something rather
 * than all of it: `as-viewport` fills the window, so lighting the element
 * lights the whole screen.
 *
 * Unlike an element, a region is used exactly as given - `padding` is there to
 * put space around an element whose size the app doesn't decide, and a region
 * is already the size the app asked for.
 */
export interface SpotlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Everything `show()` and `spotlight()` accept as a target. */
export type SpotlightTarget =
  | Element
  | SpotlightRect
  | Array<Element | SpotlightRect>
  | string
  | null;

/** Tells the two kinds of target apart. A DOMRect satisfies SpotlightRect. */
function isRect(target: Element | SpotlightRect): target is SpotlightRect {
  return !(target instanceof Element);
}

/** One-call options for `spotlight()` / `AsSpotlight.show()`. Every field
 *  matches the attribute of the same name. */
export interface SpotlightOptions {
  padding?: number;
  radius?: number;
  blur?: number;
  dim?: number;
  dismissible?: boolean;
  passThrough?: boolean;
  scrollIntoView?: boolean;
}

/** A rectangle in viewport coordinates, the shape getBoundingClientRect gives. */
interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const DEFAULT_PADDING = 8;
const DEFAULT_RADIUS = 8;
const DEFAULT_BLUR = 3;
const DEFAULT_DIM = 0.45;

/** The one instance spotlight() / clearSpotlight() drive. */
let sharedSpotlight: AsSpotlight | null = null;

/** Trims to 2dp so the generated path stays readable in devtools. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Reads "left,top,width,height" from the region attribute. */
function parseRegion(value: string | null): SpotlightRect | null {
  if (!value) return null;
  const parts = value.split(',').map(part => Number(part.trim()));
  if (parts.length !== 4 || parts.some(part => !Number.isFinite(part))) {
    devlog(`[as-spotlight] region wants "left,top,width,height", got "${value}"`);
    return null;
  }
  const [left, top, width, height] = parts;
  return { left, top, width, height };
}

function rectBox(rect: SpotlightRect): Box {
  return {
    left: rect.left,
    top: rect.top,
    right: rect.left + rect.width,
    bottom: rect.top + rect.height,
  };
}

function padBox(rect: DOMRect, pad: number): Box {
  return {
    left: rect.left - pad,
    top: rect.top - pad,
    right: rect.right + pad,
    bottom: rect.bottom + pad,
  };
}

function boxesTouch(a: Box, b: Box): boolean {
  return a.left <= b.right && b.left <= a.right && a.top <= b.bottom && b.top <= a.bottom;
}

/**
 * Merges overlapping boxes into one. Two separate holes that overlap would
 * paint their overlap solid again - both winding rules count that region as
 * inside the shape - so overlapping targets have to become a single hole.
 */
function mergeBoxes(boxes: Box[]): Box[] {
  const merged: Box[] = [];
  for (const box of boxes) {
    let current = box;
    for (let i = merged.length - 1; i >= 0; i--) {
      if (!boxesTouch(current, merged[i])) continue;
      const other = merged.splice(i, 1)[0];
      current = {
        left: Math.min(current.left, other.left),
        top: Math.min(current.top, other.top),
        right: Math.max(current.right, other.right),
        bottom: Math.max(current.bottom, other.bottom),
      };
    }
    merged.push(current);
  }
  return merged;
}

/**
 * A rounded rectangle drawn anti-clockwise, so it cuts a hole out of the
 * clockwise outer rectangle it follows. Winding does the work, which means no
 * `evenodd` argument - support for that inside `path()` is patchier than
 * `path()` itself.
 */
function holePath(box: Box, radius: number): string {
  const x = round(box.left);
  const y = round(box.top);
  const w = round(box.right - box.left);
  const h = round(box.bottom - box.top);
  const r = round(Math.max(0, Math.min(radius, w / 2, h / 2)));
  const arc = `A${r} ${r} 0 0 0`;
  return `M${x + r} ${y} ${arc} ${x} ${y + r}`
    + ` V${y + h - r} ${arc} ${x + r} ${y + h}`
    + ` H${x + w - r} ${arc} ${x + w} ${y + h - r}`
    + ` V${y + r} ${arc} ${x + w - r} ${y}`
    + ` Z`;
}

/**
 * querySelectorAll that can step into open shadow roots. Segments are split
 * on `>>>`, and each one but the last selects hosts to descend into:
 *   'as-header >>> #undo-button'
 */
function deepQueryAll(selector: string): Element[] {
  const parts = selector.split('>>>').map(part => part.trim()).filter(Boolean);
  if (!parts.length) return [];

  let roots: ParentNode[] = [document];
  for (const part of parts.slice(0, -1)) {
    const next: ParentNode[] = [];
    for (const root of roots) {
      for (const host of root.querySelectorAll(part)) {
        if (host.shadowRoot) next.push(host.shadowRoot);
        else devlog(`[as-spotlight] "${part}" has no open shadow root to look inside`);
      }
    }
    roots = next;
  }

  const last = parts[parts.length - 1];
  const found: Element[] = [];
  for (const root of roots) found.push(...root.querySelectorAll(last));
  return found;
}

export class AsSpotlight extends HTMLElement {
  static tag = 'as-spotlight';

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _targets: Array<Element | SpotlightRect> = [];
  private _resizeObserver: ResizeObserver | null = null;
  private _mutationObserver: MutationObserver | null = null;
  private _frame = 0;
  private _tracking = false;

  static get observedAttributes(): string[] {
    return ['target', 'region', 'open', 'padding', 'radius', 'blur', 'dim'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsSpotlight._sheet,
    ];
    // No content and no slot: the layer is the whole component. Anything that
    // has to show alongside the highlight is a second target instead, which
    // keeps it in the app's own DOM where its styling already works.
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  connectedCallback(): void {
    this.addEventListener('click', this._handleClick);
    window.addEventListener('keydown', this._handleKeydown);
    if (this.hasAttribute('open')) this._syncOpen();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this._handleClick);
    window.removeEventListener('keydown', this._handleKeydown);
    this._stopTracking();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
        this._syncOpen();
        break;
      case 'target':
      case 'region':
        this._resolveTargets();
        this._observeTargets();
        this._apply();
        break;
      case 'blur':
        this.style.setProperty('--spotlight-blur', `${this._number('blur', DEFAULT_BLUR)}px`);
        break;
      case 'dim':
        this.style.setProperty('--spotlight-dim', String(this._number('dim', DEFAULT_DIM)));
        break;
      default:
        this._apply();
    }
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /** The elements and regions currently revealed. */
  get targets(): Array<Element | SpotlightRect> {
    return [...this._targets];
  }

  /** Sets the target(s) directly: elements a selector can't reach, regions,
   *  or a mix of both. */
  set targets(value: SpotlightTarget) {
    this._targets = this._normalise(value);
    // A directly-set target wins over the attributes, so drop those rather
    // than leave two sources of truth behind.
    if (this.hasAttribute('target')) this.removeAttribute('target');
    if (this.hasAttribute('region')) this.removeAttribute('region');
    this._observeTargets();
    this._apply();
  }

  get open(): boolean {
    return this.hasAttribute('open');
  }

  set open(value: boolean) {
    this.toggleAttribute('open', value);
  }

  /**
   * Shows the spotlight, moving it to a new target first if one is given.
   * Called on an element that isn't in the page yet, it adds itself to the
   * body - a spotlight has nowhere else meaningful to live.
   */
  show(target?: SpotlightTarget, options: SpotlightOptions = {}): void {
    if (!this.isConnected) document.body.append(this);
    this._applyOptions(options);
    if (target !== undefined) {
      if (typeof target === 'string') this.setAttribute('target', target);
      else this.targets = target;
    }
    if (this.hasAttribute('open')) this._syncOpen();
    else this.setAttribute('open', '');
  }

  /** Hides the spotlight. Safe to call when it is already hidden. */
  hide(): void {
    this.removeAttribute('open');
  }

  /** Re-measures the target. Only needed for a move the tracking misses. */
  refresh(): void {
    this._apply();
  }

  /**
   * The shared spotlight - one per page, added to the body the first time it
   * is asked for. What `spotlight()` uses, so an app needs neither the element
   * in its markup nor a reference of its own.
   */
  static shared(): AsSpotlight {
    if (!sharedSpotlight) {
      sharedSpotlight = document.createElement(AsSpotlight.tag) as AsSpotlight;
      sharedSpotlight.setAttribute('data-shared', '');
    }
    if (!sharedSpotlight.isConnected) document.body.append(sharedSpotlight);
    return sharedSpotlight;
  }

  // ==========================================================================
  // Attributes and targets
  // ==========================================================================

  private _number(name: string, fallback: number): number {
    const raw = this.getAttribute(name);
    if (raw === null || raw.trim() === '') return fallback;
    const value = Number(raw);
    return Number.isFinite(value) ? value : fallback;
  }

  private _applyOptions(options: SpotlightOptions): void {
    const { padding, radius, blur, dim, dismissible, passThrough, scrollIntoView } = options;
    if (padding !== undefined) this.setAttribute('padding', String(padding));
    if (radius !== undefined) this.setAttribute('radius', String(radius));
    if (blur !== undefined) this.setAttribute('blur', String(blur));
    if (dim !== undefined) this.setAttribute('dim', String(dim));
    if (dismissible !== undefined) this.toggleAttribute('dismissible', dismissible);
    if (passThrough !== undefined) this.toggleAttribute('pass-through', passThrough);
    if (scrollIntoView !== undefined) this.toggleAttribute('scroll-into-view', scrollIntoView);
  }

  private _normalise(value: SpotlightTarget): Array<Element | SpotlightRect> {
    if (!value) return [];
    if (typeof value === 'string') return deepQueryAll(value);
    return (Array.isArray(value) ? value : [value]).filter(Boolean);
  }

  private _resolveTargets(): void {
    const selector = this.getAttribute('target');
    const region = this.getAttribute('region');
    if (selector === null && region === null) return;

    // Both attributes can be set at once - an element and a region beside it.
    const found: Array<Element | SpotlightRect> = selector ? deepQueryAll(selector) : [];
    const rect = parseRegion(region);
    if (rect) found.push(rect);

    this._targets = found;
    if (!found.length && selector) devlog(`[as-spotlight] nothing matches "${selector}"`);
  }

  // ==========================================================================
  // Open and close
  // ==========================================================================

  private _syncOpen(): void {
    if (!this.hasAttribute('open')) {
      this._stopTracking();
      this._dispatch(AS_SPOTLIGHT_EVENTS.HIDDEN);
      return;
    }

    // Setting the attribute on an element that isn't in the page yet is
    // normal - markup is parsed that way, and so is
    // createElement + setAttribute. connectedCallback runs this again once it
    // lands, which is the point to act on.
    if (!this.isConnected) return;

    if (this.hasAttribute('target') || this.hasAttribute('region')) this._resolveTargets();
    this._startTracking();
    this._apply();
    const first = this._targets[0];
    // A region is already in viewport coordinates: scrolling would move the
    // page out from under it.
    if (this.hasAttribute('scroll-into-view') && first && !isRect(first)) {
      first.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    this._dispatch(AS_SPOTLIGHT_EVENTS.SHOWN, { targets: this.targets });
  }

  // ==========================================================================
  // Tracking
  // ==========================================================================

  private _startTracking(): void {
    if (this._tracking) return;
    this._tracking = true;
    window.addEventListener('resize', this._schedule);
    // Capture, so scrolling inside a step panel counts too, not just the page.
    window.addEventListener('scroll', this._schedule, true);
    this._resizeObserver = new ResizeObserver(this._schedule);
    this._mutationObserver = new MutationObserver(this._schedule);
    // A target can move without resizing - a panel opening above it, a step
    // switching, the element being taken out of the page. Cheap enough,
    // because _schedule collapses a whole frame of mutations into one
    // re-measure.
    this._mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'open', 'active'],
    });
    this._observeTargets();
  }

  private _observeTargets(): void {
    if (!this._resizeObserver) return;
    this._resizeObserver.disconnect();
    this._resizeObserver.observe(document.documentElement);
    this._targets.forEach(target => {
      if (!isRect(target)) this._resizeObserver!.observe(target);
    });
  }

  private _stopTracking(): void {
    this._tracking = false;
    window.removeEventListener('resize', this._schedule);
    window.removeEventListener('scroll', this._schedule, true);
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._mutationObserver?.disconnect();
    this._mutationObserver = null;
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = 0;
  }

  private _schedule = (): void => {
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = 0;
      this._apply();
    });
  };

  // ==========================================================================
  // Drawing
  // ==========================================================================

  private _apply(): void {
    if (!this.hasAttribute('open') || !this.isConnected) return;

    const padding = this._number('padding', DEFAULT_PADDING);
    const radius = this._number('radius', DEFAULT_RADIUS);
    const boxes = mergeBoxes(this._targets.flatMap(target => {
      // A region is taken as given; only an element gets padding.
      if (isRect(target)) {
        return target.width > 0 && target.height > 0 ? [rectBox(target)] : [];
      }
      if (!target.isConnected) return [];
      const rect = target.getBoundingClientRect();
      // An element that isn't being drawn - display: none, or sitting in a
      // step panel that isn't showing - measures 0x0. Drop it here, before
      // padding: padding an empty rect turns it into a small hole in the top
      // left corner, which looks like a bug and hides nothing.
      return rect.width > 0 && rect.height > 0 ? [padBox(rect, padding)] : [];
    }));

    if (this._targets.length && !boxes.length) {
      this._targetLost();
      return;
    }

    const width = round(window.innerWidth);
    const height = round(window.innerHeight);
    const outer = `M0 0 H${width} V${height} H0 Z`;
    const holes = boxes.map(box => holePath(box, radius)).join(' ');
    // path() units are the host's own box, and the host is fixed at inset 0,
    // so they line up 1:1 with getBoundingClientRect's viewport coordinates.
    this.style.clipPath = `path("${outer} ${holes}")`;
  }

  private _targetLost(): void {
    devlog('[as-spotlight] target left the page - closing');
    this._dispatch(AS_SPOTLIGHT_EVENTS.TARGET_LOST);
    this.hide();
  }

  // ==========================================================================
  // Input
  // ==========================================================================

  private _handleClick = (event: MouseEvent): void => {
    // Only a click that landed on the layer itself counts - a click on a
    // revealed target passes through the hole and never reaches here.
    if (event.composedPath()[0] !== this) return;
    this._dispatch(AS_SPOTLIGHT_EVENTS.OUTSIDE_CLICK);
    if (this.hasAttribute('dismissible')) this.hide();
  };

  private _handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (!this.hasAttribute('open') || !this.hasAttribute('dismissible')) return;
    event.preventDefault();
    this.hide();
  };

  private _dispatch(name: string, detail: Record<string, unknown> = {}): void {
    sendEv(this, name, { detail });
  }
}

/**
 * Spotlights an element through the shared instance - the short way in, with
 * no element to add to the page and nothing to hold on to.
 *
 *   spotlight('#importGroup', { caption: 'Drop your parts in here.' });
 */
export function spotlight(target: SpotlightTarget, options: SpotlightOptions = {}): AsSpotlight {
  const el = AsSpotlight.shared();
  el.show(target, options);
  return el;
}

/** Hides the shared spotlight. Does nothing if there isn't one. */
export function clearSpotlight(): void {
  sharedSpotlight?.hide();
}

declare global {
  interface HTMLElementTagNameMap {
    'as-spotlight': AsSpotlight;
  }
}

if (!customElements.get(AsSpotlight.tag)) {
  customElements.define(AsSpotlight.tag, AsSpotlight);
} else {
  console.warn('as-spotlight already defined');
}
